import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { notification } from 'antd';
import { useLocation } from 'react-router-dom';

import useStores from '$src/hooks/useStores';
import useFeature from '$src/hooks/useFeature';
import isWebView, { isRingAppWebView } from '$src/utils/user-agent';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import { history } from '$src/stores/history';

import Logo from '$src/components/Logo';
import BackButton from '$src/components/BackButton';

import UserArea from './UserArea';
import Sider from './Sider';
import Content from './Content';
import Subheader from './Subheader';
import Footer from './Footer';

import './index.less';

const HIDE_BACK_BUTTON_FEATURE = 'stark-feature-next-hide-back-button';

function Layout({ children, sider, banner, hideBack, subheader }) {
  const { sessionStore } = useStores();
  const { isMobile } = sessionStore;
  const locationParam = useLocation();
  const displayingSublevel = locationParam.state?.displayingSublevel;
  const [siderCollapsed, setSiderCollapsed] = useState(!locationParam.state?.sider);
  const closingTooltips = () => notification.destroy();
  const app = document.getElementById('app');
  const [isHideBackButtonEnabled] = useFeature(HIDE_BACK_BUTTON_FEATURE);

  // The Ring App renders its own back button around this WebView, so ours is a
  // confusing duplicate. Callers can still opt out per page via `hideBack`.
  const hideBackButton = hideBack || (isHideBackButtonEnabled && isRingAppWebView());

  const onLogout = () => {
    sessionStore.onLogout();
  };

  const backButtonHandler = () => {
    closingTooltips();

    if (displayingSublevel) {
      history.goBack();
    } else if (isMobile && siderCollapsed) {
      setSiderCollapsed(!siderCollapsed);
    } else {
      handleBackToRingDashboard(isMobile);
    }
  };

  useEffect(
    () => () => {
      closingTooltips();
    },
    [],
  );

  useEffect(() => {
    app?.scrollTo(0, 0);
  }, [siderCollapsed, app]);

  return (
    <>
      <header key="header" className="app-header">
        {!isWebView() && (
          <div className="app-header__row">
            <div className="app-header__row--left">
              <Logo logoName="STARK" version="1.0" />
            </div>
            <div className="app-header__row--right">{sessionStore.user && <UserArea user={sessionStore.user} onLogout={onLogout} />}</div>
          </div>
        )}
        {(!hideBackButton || subheader) && (isMobile || !displayingSublevel) && (
          <div className="app-header__row">
            {!hideBackButton && (
              <div className="layout__back-button">
                <BackButton onClick={backButtonHandler} />
              </div>
            )}
            {subheader}
          </div>
        )}
      </header>
      <div className="layout">
        <div className="layout__body">
          {sider && <Sider collapsed={isMobile && siderCollapsed}>{sider}</Sider>}
          <Content hide={isMobile && !siderCollapsed}>
            {(!hideBackButton || !!banner) && (
              <Subheader>
                {!hideBackButton && !isMobile && displayingSublevel && (
                  <div className="layout__back-button">
                    <BackButton onClick={backButtonHandler} />
                  </div>
                )}
                {!isMobile && banner}
              </Subheader>
            )}

            {children}
          </Content>
        </div>
        {banner && <Footer> {isMobile && banner}</Footer>}
      </div>
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  banner: PropTypes.node,
  sider: PropTypes.node,
  hideBack: PropTypes.bool,
};

Layout.defaultProps = {
  children: null,
  banner: null,
  sider: null,
};

export default observer(Layout);
